import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import ApiBase from './ApiBase';
import type { ApiResponse } from './ApiResponse';
import type { ApiV2Response, ApiV2Unpaged } from './ApiV2Response';
import { isV2Format, isV2Paged, normalizeV2Paged, normalizeV2Unpaged } from './ApiV2Response';
import type { HeadersMap } from './HeadersMap';

/**
 * V1 REST client: requests through the shared transport ({@link ApiBase}) and
 * the standardized `{ meta, data }` `ApiResponse` envelope (including the proxied
 * V2 shape detected by `isV2Format`). For services the app calls **directly**
 * (bare envelope, no proxy) use `ApiV2` instead.
 */
export default class Api extends ApiBase {
  public static disableInterceptorErrors = false;

  public static emptyResponse<T>(data: T | null = null): Promise<ApiResponse<T>> {
    return Promise.resolve<ApiResponse<T>>({
      data: data as T,
      hasNextPage: false,
      hasPreviousPage: false,
      totalCount: 0,
      totalPages: 0,
      status: 200,
      headers: null!,
      meta: {
        code: 200,
        message: '',
        success: true,
      },
    });
  }

  protected static async get<T>(url: string, config?: AxiosRequestConfig | undefined): Promise<ApiResponse<T>> {
    const res = await Api.makeRequest('GET', config, url);
    return Api.handleResponse<T>(res);
  }

  protected static async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig | undefined): Promise<ApiResponse<T>> {
    const res = await Api.makeRequest('POST', config, url, data);
    return Api.handleResponse<T>(res);
  }

  protected static async postRaw(url: string, data?: unknown, config?: AxiosRequestConfig | undefined) {
    const res = await Api.makeRequest('POST', config, url, data);
    return res;
  }

  protected static async delete<T>(url: string, config?: AxiosRequestConfig | undefined): Promise<ApiResponse<T>> {
    const res = await Api.makeRequest('DELETE', config, url);
    return Api.handleResponse<T>(res);
  }

  protected static async head<T>(url: string, config?: AxiosRequestConfig | undefined): Promise<ApiResponse<T>> {
    const res = await Api.makeRequest('HEAD', config, url);
    return Api.handleResponse<T>(res);
  }

  protected static async options<T>(url: string, config?: AxiosRequestConfig | undefined): Promise<ApiResponse<T>> {
    const res = await Api.makeRequest('OPTIONS', config, url);
    return Api.handleResponse<T>(res);
  }

  protected static async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig | undefined): Promise<ApiResponse<T>> {
    const res = await Api.makeRequest('PATCH', config, url, data);
    return Api.handleResponse<T>(res);
  }

  protected static async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig | undefined): Promise<ApiResponse<T>> {
    const res = await Api.makeRequest('PUT', config, url, data);
    return Api.handleResponse<T>(res);
  }

  protected static async postFile<T>(
    url: string,
    parameterName: string,
    file: File | File[],
    progresCb: (progress: number) => void,
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    if (Array.isArray(file)) {
      file.forEach((f) => {
        formData.append(parameterName, f);
      });
    } else {
      formData.append(parameterName, file);
    }

    return this.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => progresCb(event.total ? event.loaded / event.total : 0),
      signal: new AbortController().signal, // don't cancel multiple file requests
    });
  }

  private static handleResponse<T>(res: AxiosResponse): ApiResponse<T> {
    const status = res.status;
    const headers = res.headers as HeadersMap;

    // For some reason, the api sometimes returns an empty response - don't error on those
    if (!res.data) {
      return res.data;
    }

    if (res.config.responseType === 'arraybuffer') {
      return {
        meta: { success: true, code: status, message: '' },
        data: res.data,
        status,
        headers,
      } as ApiResponse<T>;
    }

    const payload = res.data as ApiResponse<T> | ApiV2Response<T>;

    let response: ApiResponse<T> | ApiResponse<T[]>;

    if (isV2Format(headers)) {
      response = isV2Paged<T>(payload)
        ? normalizeV2Paged<T>(payload, status, headers)
        : normalizeV2Unpaged<T>(payload as ApiV2Unpaged<T>, status, headers);
    } else {
      const v1 = payload as ApiResponse<T>;
      response = {
        ...v1,
        status,
        headers,
      };
    }

    if (response?.meta && !response?.meta?.success) {
      throw response;
    }

    return response as ApiResponse<T>;
  }
}
